import React, { useState, useEffect, useCallback, useRef } from 'react';
import { Link } from 'react-router-dom';
import { apiService, DashboardStats, RecentTransaction } from '../services/api';
import Loader from './Loader';
import EmptyState from './EmptyState';
import { useNotifications } from './NotificationSystem';
import { handleApiError } from '../utils/errorHandler';
import './Dashboard.css';

// Remove duplicate interfaces - they're now imported from api.ts

interface DashboardProps {
  recurringTransactionsEnabled: boolean;
}

const Dashboard: React.FC<DashboardProps> = ({ recurringTransactionsEnabled }) => {
  const [stats, setStats] = useState<DashboardStats>({
    totalBalance: 0,
    monthlyIncome: 0,
    monthlyExpenses: 0,
    totalTransactions: 0,
    netIncome: 0,
    savingsRate: 0,
    accountBalances: [],
    categorySummary: []
  });
  const [recentTransactions, setRecentTransactions] = useState<RecentTransaction[]>([]);
  const [loading, setLoading] = useState(true);
  const { showError } = useNotifications();
  const abortControllerRef = useRef<AbortController | null>(null);
  const isMountedRef = useRef(true);
  const hasFetchedRef = useRef(false); // Prevent duplicate fetches
  const isFetchingRef = useRef(false); // Prevent concurrent requests

  const fetchDashboardData = useCallback(async () => {
    // CRITICAL: Don't make API calls if not authenticated
    if (!apiService.isAuthenticated()) {
      console.warn('Dashboard: Not authenticated, skipping API calls');
      setLoading(false);
      return;
    }

    // Prevent duplicate/concurrent requests
    if (isFetchingRef.current) {
      console.log('Dashboard: Request already in progress, skipping');
      return;
    }

    // Cancel any previous request
    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
    }

    // Create new AbortController for this request
    abortControllerRef.current = new AbortController();
    isFetchingRef.current = true;

    try {
      const [statsData, recentData] = await Promise.all([
        apiService.getDashboardStats(),
        apiService.getRecentTransactions(5)
      ]);

      // Only update state if component is still mounted
      if (isMountedRef.current) {
        setStats(statsData);
        setRecentTransactions(recentData);
        hasFetchedRef.current = true;
      }
    } catch (error: any) {
      // Don't handle errors if component is unmounted
      if (!isMountedRef.current) {
        return;
      }

      // If 401, the global handler will redirect - don't show error
      if (error.status === 401 || error.errorCode === 'UNAUTHORIZED') {
        console.log('Dashboard: 401 detected, redirecting to login');
        return;
      }

      // Only show error if not aborted
      if (error.name !== 'AbortError') {
        const appError = handleApiError(error);
        showError('Loading Error', appError.message);
      }
    } finally {
      isFetchingRef.current = false;
      if (isMountedRef.current) {
        setLoading(false);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []); // Empty deps - only create once, use showError from closure

  useEffect(() => {
    isMountedRef.current = true;
    hasFetchedRef.current = false;
    isFetchingRef.current = false;
    
    // Only fetch once on mount if authenticated
    if (apiService.isAuthenticated() && !hasFetchedRef.current) {
      fetchDashboardData();
    } else {
      setLoading(false);
    }

    // Cleanup on unmount
    return () => {
      isMountedRef.current = false;
      isFetchingRef.current = false;
      if (abortControllerRef.current) {
        abortControllerRef.current.abort();
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []); // Empty deps - only run once on mount

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('en-IN', {
      style: 'currency',
      currency: 'INR',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(amount);
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-IN', {
      day: 'numeric',
      month: 'short',
      year: 'numeric'
    });
  };

  if (loading) {
    return (
      <div className="dashboard">
        <Loader message="Loading dashboard..." size="large" />
      </div>
    );
  }

  return (
    <div className="dashboard">
      <div className="dashboard-header">
        <h1>Dashboard</h1>
        <p>Welcome back! Here's your financial overview.</p>
      </div>

      {/* Stats Cards */}
      <div className="stats-grid">
        <div className="stat-card balance-card">
          <div className="stat-icon">💰</div>
          <div className="stat-content">
            <h3>Total Balance</h3>
            <div className="stat-value">{formatCurrency(stats.totalBalance)}</div>
            <div className="stat-change positive">+12.5% from last month</div>
          </div>
        </div>

        <div className="stat-card income-card">
          <div className="stat-icon">📈</div>
          <div className="stat-content">
            <h3>Monthly Income</h3>
            <div className="stat-value">{formatCurrency(stats.monthlyIncome)}</div>
            <div className="stat-change positive">+8.2% from last month</div>
          </div>
        </div>

        <div className="stat-card expense-card">
          <div className="stat-icon">📉</div>
          <div className="stat-content">
            <h3>Monthly Expenses</h3>
            <div className="stat-value">{formatCurrency(stats.monthlyExpenses)}</div>
            <div className="stat-change negative">+3.1% from last month</div>
          </div>
        </div>

        <div className="stat-card transaction-card">
          <div className="stat-icon">🔄</div>
          <div className="stat-content">
            <h3>Total Transactions</h3>
            <div className="stat-value">{stats.totalTransactions}</div>
            <div className="stat-change positive">+15 this week</div>
          </div>
        </div>
      </div>

      {/* Quick Actions */}
      <div className="quick-actions">
        <h2>Quick Actions</h2>
        <div className="actions-grid">
          <Link to="/app/transactions" className="action-card">
            <div className="action-icon">➕</div>
            <h3>Add Transaction</h3>
            <p>Record income or expense</p>
          </Link>
          
          {recurringTransactionsEnabled && (
            <Link to="/app/recurring" className="action-card">
              <div className="action-icon">🔄</div>
              <h3>Recurring</h3>
              <p>Set up recurring payments</p>
            </Link>
          )}
          
          <Link to="/app/cash" className="action-card">
            <div className="action-icon">💳</div>
            <h3>Cash Management</h3>
            <p>Manage your cash flow</p>
          </Link>
          
          <Link to="/app/reports" className="action-card">
            <div className="action-icon">📊</div>
            <h3>View Reports</h3>
            <p>Analyze your finances</p>
          </Link>
          
          <Link to="/app/import-export" className="action-card">
            <div className="action-icon">📁</div>
            <h3>Import/Export</h3>
            <p>Import or export data</p>
          </Link>
        </div>
      </div>

      {/* Recent Transactions */}
      <div className="recent-transactions">
        <div className="section-header">
          <h2>Recent Transactions</h2>
          <Link to="/app/transactions" className="view-all-link">
            View All →
          </Link>
        </div>
        
        <div className="transactions-list">
          {recentTransactions.length === 0 ? (
            <EmptyState
              icon="📊"
              title="No recent transactions"
              message="Your recent transactions will appear here once you start adding transactions."
            />
          ) : (
            recentTransactions.map((transaction) => (
            <div key={transaction.id} className="transaction-item">
              <div className="transaction-icon">
                {transaction.type === 1 ? '📈' : '📉'}
              </div>
              <div className="transaction-details">
                <h4>{transaction.description}</h4>
                <p className="transaction-category">{transaction.category}</p>
                <p className="transaction-date">{formatDate(transaction.entryDate)}</p>
              </div>
              <div className={`transaction-amount ${transaction.type === 1 ? 'income' : 'expense'}`}>
                {transaction.type === 1 ? '+' : '-'}{formatCurrency(transaction.amount)}
              </div>
            </div>
            ))
          )}
        </div>
      </div>

      {/* Monthly Overview */}
      <div className="monthly-overview">
        <h2>This Month Overview</h2>
        <div className="overview-cards">
          <div className="overview-card">
            <h3>Income vs Expenses</h3>
            <div className="progress-bar">
              <div className="progress-fill income" style={{ width: '100%' }}>
                <span>Income: {formatCurrency(stats.monthlyIncome)}</span>
              </div>
              <div className="progress-fill expense" style={{ width: `${(stats.monthlyExpenses / stats.monthlyIncome) * 100}%` }}>
                <span>Expenses: {formatCurrency(stats.monthlyExpenses)}</span>
              </div>
            </div>
            <div className="net-amount">
              Net: {formatCurrency(stats.netIncome)}
            </div>
          </div>
          
          <div className="overview-card">
            <h3>Savings Rate</h3>
            <div className="savings-rate">
              <div className="rate-value">
                {Math.round(stats.savingsRate)}%
              </div>
              <div className="rate-label">of income saved</div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Dashboard;
